"use client";

import React, { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import "./sap-btp-integration.css";

/* Images — public/assets/images/ se serve hoti hain */
const IMG = "/assets/images";
/* file names me space / special characters ho sakte hain, isliye encode karke path banate hain */
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("sap-btp-integration-hero.jpg");
const imgCta = img("cta-img.jpg");

/* All styling lives in sap-btp-integration.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--bi-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "search" | "briefcase" | "target" | "trend" | "people" | "send"
  | "nodes" | "gear" | "cube" | "flow" | "bolt" | "bars"
  | "link" | "window" | "cloud" | "db" | "shield" | "shieldcheck"
  | "doc" | "wifi" | "refresh" | "user" | "clock" | "globe";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    search: (<><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.2-4.2" /></>),
    briefcase: (<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 13h18" /></>),
    target: (<><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" /></>),
    trend: (<><path d="M3 17l5-5 4 4 8-9" /><path d="M15 7h5v5" /></>),
    people: (<><circle cx="9" cy="8" r="3" /><path d="M3 20c.4-3.6 2.6-5.4 6-5.4s5.6 1.8 6 5.4M16 5.5a3 3 0 0 1 0 5.8M18 14.8c1.8.6 2.8 2.3 3 5.2" /></>),
    send: <path d="M5 12h14M13 6l6 6-6 6" />,
    nodes: (<><circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="6" r="2.2" /><circle cx="6" cy="18" r="2.2" /><circle cx="18" cy="18" r="2.2" /><circle cx="12" cy="12" r="2.4" /><path d="m7.6 7.6 2.7 2.7M16.4 7.6l-2.7 2.7M7.6 16.4l2.7-2.7M16.4 16.4l-2.7-2.7" /></>),
    gear: (<><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" /></>),
    cube: (<><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>),
    flow: (<><circle cx="6" cy="5" r="2" /><circle cx="18" cy="12" r="2" /><circle cx="6" cy="19" r="2" /><path d="M6 7v3a2 2 0 0 0 2 2h8M6 12v5" /></>),
    bolt: <path d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z" />,
    bars: <path d="M5 20V12M11 20V4M17 20V9M3 20h18" />,
    link: (<><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" /><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" /></>),
    window: (<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M7 6.5h.01M10 6.5h.01" /></>),
    cloud: <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9.5a4.2 4.2 0 0 1-.5 8.5H7Z" />,
    db: (<><ellipse cx="12" cy="5.5" rx="7" ry="2.8" /><path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" /></>),
    shield: (<><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" /></>),
    shieldcheck: (<><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" /><path d="m9 12 2.2 2.2L15.5 10" /></>),
    doc: (<><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 9h6M9 13h6M9 17h3" /></>),
    wifi: (<><path d="M4 9.5a12 12 0 0 1 16 0M7 13a7.5 7.5 0 0 1 10 0M10 16.5a3 3 0 0 1 4 0" /><circle cx="12" cy="19.5" r=".8" /></>),
    refresh: (<><path d="M20 12a8 8 0 0 0-14.5-4.7L4 9M4 4v5h5" /><path d="M4 12a8 8 0 0 0 14.5 4.7L20 15M20 20v-5h-5" /></>),
    user: (<><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.6-4 3-6 7-6s6.4 2 7 6" /></>),
    clock: (<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>),
    globe: (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.6 2.6 3.8 5.6 3.8 9S14.6 18.4 12 21c-2.6-2.6-3.8-5.6-3.8-9S9.4 5.6 12 3Z" /></>),
  };

  return (
    <svg
      className="bi-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const capabilities: { icon: IconKey; title: string; text: string; href: string }[] = [
  { icon: "nodes", title: "SAP Integration Suite", text: "Design and manage integration flows connecting SAP and non-SAP applications.", href: "/solutions/sap-integration-suite" },
  { icon: "gear", title: "API Management", text: "Structure, expose, secure and govern APIs to support controlled information exchange.", href: "/solutions/api-management" },
  { icon: "cube", title: "Application Extensions", text: "Develop side-by-side extensions and custom applications on SAP BTP while keeping the core clean.", href: "/solutions/application-extensions" },
  { icon: "flow", title: "Workflow Automation", text: "Connect business events, approvals and tasks across applications to reduce manual effort.", href: "/solutions/workflow-automation" },
  { icon: "bolt", title: "Event-Driven Integration", text: "Enable real-time, event-based integration for faster and more reliable business processes.", href: "/solutions/event-driven-integration" },
  { icon: "bars", title: "Monitoring & Governance", text: "Improve observability, error handling and governance for robust and secure integrations.", href: "/solutions/monitoring-governance" },
];

const topApps = ["SAP S/4HANA", "SuccessFactors", "SAP Ariba", "Other SAP Apps"];

const bottomApps: { icon: IconKey; label: string }[] = [
  { icon: "people", label: "CRM" },
  { icon: "bars", label: "SFA" },
  { icon: "cloud", label: "Cloud Apps" },
  { icon: "doc", label: "Legacy Systems" },
  { icon: "shield", label: "Partners & Suppliers" },
  { icon: "wifi", label: "IoT & Devices" },
];

const solutions: { icon: IconKey; title: string; text: string }[] = [
  { icon: "link", title: "SAP-to-SAP Integration", text: "Connect ERP and other SAP applications." },
  { icon: "nodes", title: "SAP-to-Non-SAP Integration", text: "Link SAP with CRM, SFA, logistics, HR and industry apps." },
  { icon: "gear", title: "API-Led Connectivity", text: "Establish reusable and governed interfaces." },
  { icon: "flow", title: "Business Process Automation", text: "Coordinate approvals, notifications and workflows." },
  { icon: "cube", title: "Custom Business Applications", text: "Build extensions for unique business requirements." },
  { icon: "shieldcheck", title: "Integration Governance", text: "Define standards, ownership and lifecycle management." },
];

const steps = [
  { title: "Map the process", text: "Understand business events, information flow and system dependencies." },
  { title: "Assess the landscape", text: "Review applications, interfaces, APIs, security and integration requirements." },
  { title: "Design the architecture", text: "Select integration patterns, error handling and governance models." },
  { title: "Build and validate", text: "Implement and test interfaces with end-to-end process scenarios." },
  { title: "Monitor and improve", text: "Track performance, resolve issues and identify new opportunities." },
];

const benefits: { icon: IconKey; text: string }[] = [
  { icon: "clock", text: "Faster information flow" },
  { icon: "trend", text: "Lower manual effort" },
  { icon: "db", text: "Improved data consistency" },
  { icon: "refresh", text: "Greater process reliability" },
  { icon: "user", text: "Better user experience" },
  { icon: "bars", text: "Stronger application flexibility" },
];

const scenarios = [
  { title: "CRM to SAP", text: "Align customer and order data across sales and finance.", image: img("Sales-Operations.jpg"), href: "/use-cases/crm-to-sap" },
  { title: "SFA to ERP", text: "Automate order exchange and status updates.", image: img("customer-channel.jpg"), href: "/use-cases/sfa-to-erp" },
  { title: "SAP to Logistics", text: "Connect shipment and delivery visibility.", image: img("supply-chain-hero1.jpg"), href: "/use-cases/sap-to-logistics" },
  { title: "Supplier Integration", text: "Streamline purchase interfaces with confirmations.", image: img("Partnerships-Ecosystem.jpg"), href: "/use-cases/supplier-integration" },
  { title: "Finance Integration", text: "Reduce reconciliation and improve accuracy.", image: img("BILL-&-RECOGNISE.jpg"), href: "/use-cases/finance-integration" },
  { title: "Enterprise Approvals", text: "Automate cross-system approval workflows.", image: img("Project-Governance.jpg"), href: "/use-cases/enterprise-approvals" },
];

const why: { icon: IconKey; title: string; text: string }[] = [
  { icon: "briefcase", title: "Business process first", text: "We map workflows before designing integrations." },
  { icon: "globe", title: "SAP and non-SAP perspective", text: "We consider the full enterprise application landscape." },
  { icon: "shieldcheck", title: "Clean-core approach", text: "We use SAP BTP to extend without unnecessary core changes." },
  { icon: "doc", title: "Reliable and secure", text: "We focus on governance, monitoring and operational support." },
  { icon: "refresh", title: "Continuous value", text: "Integrations that enable automation, visibility and better decisions." },
];

/* ============================== PAGE ============================== */

export default function SapBtpIntegrationPage() {
  const ref = useScrollReveal();
  return (
    <div className="bi-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="bi-hero" id="top">
          <div className="bi-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }} aria-hidden="true"></div>
          <div className="bi-hero-overlay" aria-hidden="true"></div>

          <div className="bi-hero-inner">
            <div className="bi-hero-copy">
              <h1 data-reveal style={delay(0)}>
                Your systems are connected. <span>Are your business decisions?</span>
              </h1>
              <p data-reveal style={delay(1, 120)}>
                Orane helps organisations use SAP Business Technology Platform and enterprise integration to
                connect applications, automate processes and make business information more accessible.
              </p>
              <div className="bi-actions" data-reveal style={delay(2, 120)}>
                <Link href="/tellusyourproblem#tell-details" className="bi-btn bi-btn-primary">
                  Tell Us Your Integration Problem <span aria-hidden="true">→</span>
                </Link>
                <Link href="#capabilities" className="bi-btn bi-btn-outline">
                  Explore SAP BTP Capabilities <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
            {/* <p className="bi-hero-tagline" data-reveal style={delay(3, 120)}>
              Integrate | Automate | Innovate | Scale
            </p> */}
          </div>
        </section>

        {/* =========================== CAPABILITIES ========================== */}
        <section className="bi-section" id="capabilities">
          <div className="bi-container">
            <div className="bi-head bi-head--row" data-reveal>
              <h2>Our SAP BTP and integration capabilities</h2>
              <p>From integration strategy to continuous innovation, we help you connect systems with purpose.</p>
            </div>

            <div className="bi-cap-grid">
              {capabilities.map((c, i) => (
                <article className="bi-cap-card" key={c.title} data-reveal style={delay(i, 80)}>
                  <span className="bi-cap-icon"><Icon name={c.icon} /></span>
                  <h3>{c.title}</h3>
                  <p>{c.text}</p>
                  {/* <Link href={c.href} className="bi-link">Learn more <span aria-hidden="true">→</span></Link> */}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== CONNECT THE PROCESSES ==================== */}
        <section className="bi-section" id="integration-solutions">
          <div className="bi-container">
            <div className="bi-connect">
              <div className="bi-connect-copy" data-reveal>
                <h2>Connect the processes that keep your enterprise moving.</h2>
                <p>
                  Our integration services address business workflows that cross application and
                  organizational boundaries — enabling connected operations and faster decisions.
                </p>
                {/* <Link href="/solutions/integration" className="bi-link bi-link--lg">
                  Explore all integration solutions <span aria-hidden="true">→</span>
                </Link> */}
              </div>

              <div className="bi-diagram" data-reveal style={delay(1, 100)} aria-label="SAP BTP connecting SAP and non-SAP applications">
                <div className="bi-diagram-row bi-diagram-row--top">
                  {topApps.map((a) => (
                    <div className="bi-node" key={a}>
                      <span className="bi-node-icon"><Icon name="window" /></span>
                      <span className="bi-node-label">{a}</span>
                    </div>
                  ))}
                </div>
                <span className="bi-diagram-line" aria-hidden="true"></span>
                <div className="bi-hub">SAP BTP</div>
                <span className="bi-diagram-line" aria-hidden="true"></span>
                <div className="bi-diagram-row bi-diagram-row--bottom">
                  {bottomApps.map((a) => (
                    <div className="bi-node" key={a.label}>
                      <span className="bi-node-icon"><Icon name={a.icon} /></span>
                      <span className="bi-node-label">{a.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bi-solutions">
              <h3 data-reveal>Our solutions include:</h3>
              <ul className="bi-solutions-grid">
                {solutions.map((s, i) => (
                  <li key={s.title} data-reveal style={delay(i % 3, 90)}>
                    <span className="bi-solution-icon"><Icon name={s.icon} /></span>
                    <div>
                      <strong>{s.title}</strong>
                      <span>{s.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ============================= PROCESS ============================= */}
        <section className="bi-section">
          <div className="bi-container">
            <div className="bi-head bi-head--row" data-reveal>
              <h2>From integration strategy to measurable value.</h2>
              <div className="bi-head-side">
                <p>We follow a structured approach to design, implement and operate integrations.</p>
                {/* <Link href="/how-we-work" className="bi-link">See how it works <span aria-hidden="true">→</span></Link> */}
              </div>
            </div>

            <ol className="bi-steps">
              {steps.map((s, i) => (
                <li className="bi-step" key={s.title} data-reveal style={delay(i, 90)}>
                  <span className="bi-step-dot" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============================ BENEFITS ============================= */}
        <section className="bi-section">
          <div className="bi-container">
            <div className="bi-head bi-head--row" data-reveal>
              <h2>Less reconciliation. More coordinated execution.</h2>
              <p>Integration creates value when it enables connected operations and faster decisions.</p>
            </div>

            <div className="bi-benefit-grid">
              {benefits.map((b, i) => (
                <div className="bi-benefit" key={b.text} data-reveal style={delay(i, 70)}>
                  <span className="bi-benefit-icon"><Icon name={b.icon} /></span>
                  <span className="bi-benefit-text">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ SCENARIOS ============================ */}
        <section className="bi-section">
          <div className="bi-container">
            <div className="bi-head bi-head--row" data-reveal>
              <h2>Enterprise integration in action</h2>
              {/* <Link href="/use-cases" className="bi-link">Explore more use cases <span aria-hidden="true">→</span></Link> */}
            </div>

            <div className="bi-scn-grid">
              {scenarios.map((s, i) => (
                <article className="bi-scn-card" key={s.title} data-reveal style={delay(i, 70)}>
                  <div className="bi-scn-image">
                    <img src={s.image} alt="" aria-hidden="true" loading="lazy" />
                  </div>
                  <div className="bi-scn-body">
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                    {/* <Link href={s.href} className="bi-link">See how we help <span aria-hidden="true">→</span></Link> */}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================== WHY ORANE ========================== */}
        <section className="bi-section">
          <div className="bi-container">
            <div className="bi-head bi-head--row" data-reveal>
              <h2>Why Orane for SAP BTP &amp; integration?</h2>
              <p>We connect systems with a reason — to solve real business challenges.</p>
            </div>

            <div className="bi-why-grid">
              {why.map((w, i) => (
                <article className="bi-why-item" key={w.title} data-reveal style={delay(i, 80)}>
                  <span className="bi-why-icon"><Icon name={w.icon} /></span>
                  <div className="bi-why-copy">
                    <h3>{w.title}</h3>
                    <p>{w.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ FINAL CTA ============================ */}
        <section className="bi-cta" id="contact">
          <img className="bi-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="bi-cta-copy">
            <span className="bi-cta-eyebrow" data-reveal>READY TO CONNECT WHAT MATTERS?</span>
            <h2 data-reveal style={delay(1, 120)}>Tell us your integration challenge.</h2>
            <p data-reveal style={delay(2, 120)}>
              Which systems don&apos;t communicate, where teams repeat work or which process breaks between
              applications? We&apos;ll help you design an integration approach that creates real business value.
            </p>
            <div className="bi-actions" data-reveal style={delay(3, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="bi-btn bi-btn-primary">
                Tell Us Your Integration Problem <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}